import { useState, useEffect } from 'preact/hooks'
import { Plus, Calendar, Users, Pencil, Trash2, AlertCircle, Loader2, ChevronDown, ChevronRight, Link as LinkIcon, Eye } from 'lucide-preact'
import type { TouchPointViewDto } from '@/types/touchPoint'
import { type TouchPointFormData } from '@/components/webs/modals/TouchPointModal'
import { MeetingEditorPanel } from '@/components/webs/MeetingEditorPanel'
import { MeetingPage } from '@/components/webs/MeetingPage'
import { useMeetingOverlay } from '@/hooks/useMeetingOverlay'
import { WebsterBotStatusBadge } from '@/components/webs/WebsterBotStatusBadge'
import { WEBSTER_STATUS_SILENT_ON_LIST } from '@/components/webs/websterStatus'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { useTouchPointManager } from '@/hooks/useTouchPointManager'
import { useTouchPointGroups } from '@/hooks/useTouchPointGroups'
import { useMeetingBotStatus } from '@/hooks/useMeetingBotStatus'
import { buildTouchPointCreateDto, buildTouchPointUpdateDto } from '@/utils/touchPointDtos'
import { normalizeMeetingUrl } from '@/utils/meetingUrl'
import { isMeetingConcluded } from '@/utils/meetingLifecycle'
import type { WebViewDto } from '@/api/webs'
import { formatEventDateRange } from '@/utils/dateTimeFormatters'
import { useTimeZones } from '@/components/calendar/hooks/useTimeZones'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { useCompany } from '@/hooks/useCompany'
import { useUserProfile } from '@/hooks/useUserProfile'

interface Props {
  web?: WebViewDto // Optional: if not provided, it's campaign-level
  workspaceId: string
  campaignId: string
}

export function MeetingsTab({ web, workspaceId, campaignId }: Props) {
  // Determine if this is campaign-level (no web) or web-level
  const isCampaignLevel = !web
  const { updateContext } = useAIChatContextUpdater()
  const { companyId } = useCompany()
  const { getWorkspaceCapabilities } = useUserProfile()
  const { canEdit } = getWorkspaceCapabilities(companyId ?? '', workspaceId)

  // Use the shared touchpoint manager hook
  const {
    touchPoints,
    loading,
    error,
    saving,
    deleting,
    fetchTouchPoints,
    refetchQuietly,
    applyBotStatusUpdate,
    createTouchPoint,
    updateTouchPoint,
    deleteTouchPoint,
  } = useTouchPointManager({
    workspaceId,
    campaignId,
    webId: web?.Id,
    typeFilters: ['Meeting'],
    isCampaignLevel,
  })

  // Use the shared grouping hook - group by MeetingProperties.Type
  const { groupedTouchPoints, toggleGroup, isGroupExpanded } = useTouchPointGroups({
    touchPoints,
    groupBy: (tp) => tp.MeetingProperties?.Type || 'Other',
  })

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [editingTouchPoint, setEditingTouchPoint] = useState<TouchPointViewDto | null>(null)

  // Routed full-page meeting overlay (ORB-436): the open meeting and open/close nav all derive from the
  // URL, so it's deep-linkable, refresh-safe, and Back-button closable. Extracted into a hook to keep this
  // tab focused on the list UI.
  const { overlayMeeting, openMeeting, closeMeeting } = useMeetingOverlay({
    touchPoints,
    workspaceId,
    campaignId,
    companyId: companyId ?? '',
    isCampaignLevel,
    webId: web?.Id,
  })

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingTouchPoint, setDeletingTouchPoint] = useState<TouchPointViewDto | null>(null)

  const { getAbbreviation } = useTimeZones()

  // Live Webster bot-status: flip a meeting's badge in place as recall.ai advances it, no refresh.
  useMeetingBotStatus(workspaceId, (evt) => {
    applyBotStatusUpdate(evt)
    // The event carries status + hasRecording but not the transcript URL; once done, refetch quietly
    // so the recording + transcript land (in the list data and the open meeting page).
    if (evt.botStatus === 'Done' && touchPoints.some(tp => tp.Id === evt.touchPointId)) {
      void refetchQuietly()
    }
  })

  // AI context for the meetings view. With a meeting open, Webster is told which one (ORB-1021), so
  // "what did Guest say?" searches that meeting's transcript rather than asking which meeting is meant.
  const overlayMeetingId = overlayMeeting?.Id
  const viewContext = () => ({
    campaignId,
    webId: web?.Id,
    location: isCampaignLevel ? ChatLocations.CampaignMeetings : ChatLocations.WebMeetings,
    ...(overlayMeetingId
      ? { entityType: ChatEntityTypes.TouchPoint, entityId: overlayMeetingId }
      : { entityType: isCampaignLevel ? ChatEntityTypes.Campaign : ChatEntityTypes.Web, entityId: isCampaignLevel ? campaignId : web?.Id }),
    viewMode: ChatViewModes.View,
    availableFields: [],
    availableActions: ['Create Meeting', 'View Meetings'],
  })

  // Update AI context when viewing meetings
  useEffect(() => {
    updateContext(viewContext())
  }, [workspaceId, campaignId, web?.Id, isCampaignLevel, overlayMeetingId, updateContext])

  const handleCreate = () => {
    setEditingTouchPoint(null)
    setShowModal(true)
  }

  const handleEdit = (touchPoint: TouchPointViewDto) => {
    setEditingTouchPoint(touchPoint)
    setShowModal(true)
  }

  const handleSave = async (formData: TouchPointFormData, file?: File) => {
    if (editingTouchPoint) {
      // Update existing touchpoint
      const updateDto = buildTouchPointUpdateDto(formData, editingTouchPoint.ETag)
      const result = await updateTouchPoint(editingTouchPoint.Id, updateDto, 'Meeting Updated', file)

      if (result.success) {
        setShowModal(false)
        setEditingTouchPoint(null)
      }
    } else {
      // Create new touchpoint
      const createDto = buildTouchPointCreateDto(formData, campaignId, isCampaignLevel ? undefined : web!.Id)
      const result = await createTouchPoint(createDto, 'Meeting Created', file)

      if (result.success) {
        setShowModal(false)
        setEditingTouchPoint(null)
      }
    }
  }

  const handleDeleteClick = (touchPoint: TouchPointViewDto) => {
    setDeletingTouchPoint(touchPoint)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingTouchPoint) return

    const result = await deleteTouchPoint(deletingTouchPoint.Id, deletingTouchPoint.Name, 'Meeting Deleted')

    if (result.success) {
      setShowDeleteConfirm(false)
      setDeletingTouchPoint(null)
    }
  }

  return (
    <div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {isCampaignLevel ? 'Campaign Meetings' : 'Meetings'}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              {isCampaignLevel
                ? 'Schedule and manage campaign-wide meetings'
                : 'Schedule and manage meetings for this web'
              }
            </p>
          </div>
          {canEdit && (
            <button
              onClick={handleCreate}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>New Meeting</span>
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Error loading meetings</p>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
                <button
                  onClick={fetchTouchPoints}
                  className="mt-2 text-sm font-medium text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grouped List */}
        {!loading && !error && touchPoints.length > 0 && (
          <div className="space-y-4">
            {Object.entries(groupedTouchPoints)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([type, tps]) => {
                const isExpanded = isGroupExpanded(type) || Object.keys(groupedTouchPoints).length === 1 // Default to expanded if only one group

                return (
                  <div key={type} className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(type)}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                        )}
                        <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{type}</span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full">
                          {tps.length}
                        </span>
                      </div>
                    </button>

                    {/* Group Items */}
                    {isExpanded && (
                      <div className="p-4 space-y-3 bg-white dark:bg-slate-800">
                        {tps
                          .sort((a, b) => {
                            const aStart = a.MeetingProperties?.StartTime || ''
                            const bStart = b.MeetingProperties?.StartTime || ''
                            return new Date(aStart).getTime() - new Date(bStart).getTime()
                          })
                          .map((tp) => {
                            const meeting = tp.MeetingProperties
                            const concluded = isMeetingConcluded(meeting)
                            const hasBotBadge = !!meeting?.BotStatus && !WEBSTER_STATUS_SILENT_ON_LIST.has(meeting.BotStatus)
                            return (
                            <div
                              key={tp.Id}
                              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 hover:border-primary-300 dark:hover:border-primary-700 transition-colors overflow-hidden"
                            >
                              {/* Meeting Card Header */}
                              <div className="flex items-center justify-between p-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                                      {tp.Name}
                                    </h4>
                                    {hasBotBadge && meeting && (
                                      <WebsterBotStatusBadge
                                        status={meeting.BotStatus}
                                        subCode={meeting.BotStatusSubCode}
                                        className="shrink-0"
                                      />
                                    )}
                                    {concluded && !hasBotBadge && (
                                      <span className="shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-700/60 dark:text-slate-400">
                                        Ended
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-4 mt-1">
                                    {tp.MeetingProperties?.StartTime && tp.MeetingProperties?.EndTime && tp.MeetingProperties?.TimeZone && (
                                      <div className="flex items-center space-x-1.5 text-xs text-slate-600 dark:text-slate-400">
                                        <Calendar className="w-3.5 h-3.5" />
                                        <span>
                                          {formatEventDateRange(
                                            tp.MeetingProperties.StartTime,
                                            tp.MeetingProperties.EndTime,
                                            getAbbreviation(tp.MeetingProperties.TimeZone, tp.MeetingProperties.StartTime)
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    {!concluded && tp.MeetingProperties?.MeetingUrlLink && (
                                      <div className="flex items-center space-x-1.5 text-xs text-primary-600 dark:text-primary-400">
                                        <LinkIcon className="w-3.5 h-3.5" />
                                        <a
                                          href={normalizeMeetingUrl(tp.MeetingProperties.MeetingUrlLink)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="hover:underline"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          Join
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center space-x-2 ml-4">
                                  <button
                                    onClick={() => openMeeting(tp.Id)}
                                    className="p-2 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                    title="View details"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  {canEdit && (
                                    <>
                                      <button
                                        onClick={() => { if (!concluded) handleEdit(tp) }}
                                        aria-disabled={concluded}
                                        className={concluded
                                          ? 'p-2 text-slate-300 dark:text-slate-600 rounded-lg cursor-not-allowed'
                                          : 'p-2 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors'}
                                        title={concluded ? 'This meeting has ended; view only' : 'Edit'}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteClick(tp)}
                                        className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            )
                          })}
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        )}
      </div>

      {/* Create/Edit meeting — right-side panel */}
      <MeetingEditorPanel
        open={showModal}
        touchPoint={editingTouchPoint}
        onSave={handleSave}
        onClose={() => {
          setShowModal(false)
          setEditingTouchPoint(null)
          // Reset AI context back to View mode
          updateContext(viewContext())
        }}
        saving={saving}
        isCampaignLevel={isCampaignLevel}
      />

      {/* Routed full-page recording + transcript overlay — the Eye action (ORB-436). Keyed on the meeting
          id so switching meetings remounts it, resetting per-meeting state (active line, transcript). */}
      {overlayMeeting && (
        <MeetingPage key={overlayMeeting.Id} touchPoint={overlayMeeting} workspaceId={workspaceId} onClose={closeMeeting} />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Meeting"
        message={`Are you sure you want to delete "${deletingTouchPoint?.Name}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false)
          setDeletingTouchPoint(null)
        }}
        loading={deleting}
      />
    </div>
  )
}
